import type { ReactNode } from 'react';

type BadgeColor = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'violet';
type Size = 'sm' | 'md';

interface BadgeProps {
  children: ReactNode;
  color?: BadgeColor;
  dot?: boolean;
  mono?: boolean;
  size?: Size;
}

const map: Record<BadgeColor, { bg: string; fg: string; bd: string }> = {
  neutral: { bg: 'var(--bg-2)', fg: 'var(--text-1)', bd: 'var(--border)' },
  success: {
    bg: 'var(--success-bg)',
    fg: 'var(--success)',
    bd: 'color-mix(in oklch, var(--success) 30%, transparent)'
  },
  warning: {
    bg: 'var(--warning-bg)',
    fg: 'var(--warning)',
    bd: 'color-mix(in oklch, var(--warning) 30%, transparent)'
  },
  danger: {
    bg: 'var(--danger-bg)',
    fg: 'var(--danger)',
    bd: 'color-mix(in oklch, var(--danger) 30%, transparent)'
  },
  info: {
    bg: 'var(--info-bg)',
    fg: 'var(--info)',
    bd: 'color-mix(in oklch, var(--info) 30%, transparent)'
  },
  violet: {
    bg: 'var(--violet-bg)',
    fg: 'var(--violet)',
    bd: 'color-mix(in oklch, var(--violet) 30%, transparent)'
  }
};

export function Badge({ children, color = 'neutral', dot, mono, size = 'md' }: BadgeProps) {
  const c = map[color];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: size === 'sm' ? '1px 7px' : '2px 9px',
        fontSize: size === 'sm' ? 11 : 11.5,
        fontWeight: 500,
        borderRadius: 99,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.bd}`,
        fontFamily: mono ? 'var(--font-mono)' : 'inherit',
        letterSpacing: mono ? '-0.02em' : '0',
        whiteSpace: 'nowrap'
      }}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 99,
            background: c.fg,
            flexShrink: 0
          }}
        />
      )}
      {children}
    </span>
  );
}
